/**
 * ALTIORA · Gamification dashboard page (Sprint 2 · P1)
 *
 * Displays current level, XP progress bar, streak, earned badges,
 * weekly XP curve and recent activity timeline.
 */
import { useEffect, useState, useCallback } from "react";
import {
  Award, Flame, Sparkles, Wallet, Trophy, KanbanSquare,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer,
} from "recharts";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PageShell, PageHeader, Card, Section, Metric, Loader,
  EmptyState,
} from "@/components/altiora/PremiumUI";

const BADGE_ICONS = {
  sparkles: Sparkles, trophy: Trophy, flame: Flame,
  kanban: KanbanSquare, wallet: Wallet, award: Award,
};

const XP_LABEL = {
  deal_created: "Opportunité créée",
  deal_moved_forward: "Étape franchie",
  deal_won: "Vente gagnée",
  deal_analyzed: "Coach IA consulté",
  streak: "Bonus série quotidienne",
  commission_added: "Commission ajoutée",
};

export default function GamificationPage() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gamification/state");
      setState(data);
    } catch { toast.error("Chargement impossible"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <PageShell testid="gamification-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <PageHeader
          eyebrow="Sprint 2 · Motivation quotidienne"
          title="Progression & Badges"
          subtitle="Chaque action bien menée vous rapproche du prochain palier. Continuez sur votre lancée !"
          testid="gamification-header"
        />

        {loading && <div className="py-16 flex justify-center"><Loader /></div>}

        {!loading && state && (
          <>
            {/* Level + XP + Streak */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5">
              <Card className="p-5 lg:col-span-2" testid="gamification-level-card">
                <div className="flex items-center gap-4">
                  <div className="text-4xl" aria-hidden="true">{state.level?.icon || "🌱"}</div>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest font-semibold"
                         style={{ color: "#C5A880" }}>Niveau actuel</div>
                    <div className="font-serif text-2xl leading-tight"
                         style={{ color: "#0B2545" }}>{state.level?.label}</div>
                    <div className="text-[12px] mt-1" style={{ color: "#94A3B8" }}>
                      {state.total_xp} XP · {state.level?.next_xp
                        ? `${state.level.next_xp - state.total_xp} XP avant le prochain palier`
                        : "Palier maximum atteint 🎉"}
                    </div>
                    <div className="mt-3 h-2 rounded-full overflow-hidden"
                         style={{ background: "#F1F5F9" }}>
                      <div className="h-full transition-all duration-500"
                           style={{ width: `${state.level?.progress_pct || 0}%`,
                                      background: "#C5A880" }}
                           data-testid="gamification-progress-bar" />
                    </div>
                  </div>
                </div>
              </Card>
              <Card className="p-5 flex items-center gap-4" testid="gamification-streak-card">
                <Flame size={40} style={{ color: "#F59E0B" }} aria-hidden="true" />
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold"
                       style={{ color: "#C5A880" }}>Streak actuel</div>
                  <div className="font-serif text-3xl leading-none"
                       style={{ color: "#0B2545" }}>{state.streak?.current || 0}
                    <span className="text-base ml-1" style={{ color: "#94A3B8" }}>j</span>
                  </div>
                  <div className="text-[12px] mt-1" style={{ color: "#94A3B8" }}>
                    Record · {state.streak?.longest || 0} jours
                  </div>
                </div>
              </Card>
            </div>

            {/* Key metrics */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
              <Metric label="Opportunités créées" value={state.deals_created}
                      testid="gamification-metric-created" />
              <Metric label="Ventes gagnées" value={state.deals_won}
                      accent="#16A34A" testid="gamification-metric-won" />
              <Metric label="Commissions totales"
                      value={(state.total_commissions || 0).toLocaleString("fr-CA",
                        { style: "currency", currency: "CAD", maximumFractionDigits: 0 })}
                      accent="#C5A880" testid="gamification-metric-commissions" />
              <Metric label="XP cumulés" value={state.total_xp}
                      testid="gamification-metric-xp" />
            </div>

            {/* Weekly XP + Badges */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
              <Card className="p-4 lg:col-span-2" testid="gamification-week-card">
                <Section title="XP des 7 derniers jours"
                         hint="Votre rythme d'apprentissage et d'action">
                  <div style={{ width: "100%", height: 200, minHeight: 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={(state.weekly_xp || []).map(d => ({
                        day: d.day.slice(5),   // MM-DD
                        xp: d.xp,
                      }))} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#C5A88033" />
                        <XAxis dataKey="day" tick={{ fill: "#94A3B8", fontSize: 10 }} />
                        <YAxis tick={{ fill: "#94A3B8", fontSize: 10 }} />
                        <RTooltip />
                        <Line type="monotone" dataKey="xp" stroke="#C5A880"
                              strokeWidth={2} dot={{ fill: "#0B2545", r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </Section>
              </Card>
              <Card className="p-4" testid="gamification-badges-card">
                <Section title="Badges débloqués"
                         hint={`${(state.badges || []).length} sur ${7}`}>
                  {(state.badges || []).length === 0 ? (
                    <EmptyState icon={Award} title="Aucun badge pour l'instant"
                                description="Créez votre première opportunité pour démarrer !"
                                testid="gamification-badges-empty" />
                  ) : (
                    <ul className="grid grid-cols-2 gap-2 mt-2">
                      {state.badges.map((b, i) => {
                        const Icon = BADGE_ICONS[b.icon] || Award;
                        return (
                          <li key={b.id || i}
                              data-testid={`gamification-badge-${b.badge_key}`}
                              className="rounded-lg p-3 flex items-center gap-2"
                              style={{ border: `1px solid ${b.color}55`,
                                         background: `${b.color}0F` }}>
                            <Icon size={16} style={{ color: b.color }} />
                            <span className="text-[11px] font-semibold"
                                  style={{ color: "#0B2545" }}>{b.label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </Section>
              </Card>
            </div>

            {/* Recent activity */}
            <Card className="p-4" testid="gamification-activity-card">
              <Section title="Activité récente" hint="Vos 15 dernières actions">
                {(state.recent_events || []).length === 0 ? (
                  <div className="text-[12px] italic py-6 text-center"
                       style={{ color: "#94A3B8" }}>
                    Aucune activité pour le moment.
                  </div>
                ) : (
                  <ul className="divide-y" style={{ borderColor: "#C5A88022" }}>
                    {state.recent_events.map((e, i) => (
                      <li key={e.id || i}
                          data-testid={`gamification-event-${i}`}
                          className="flex items-center justify-between py-2 text-[12px]">
                        <div>
                          <span style={{ color: "#0B2545", fontWeight: 600 }}>
                            {XP_LABEL[e.kind] || e.kind}
                          </span>
                          {e.reason && (
                            <span style={{ color: "#94A3B8" }}> · {e.reason}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          <span style={{ color: "#C5A880", fontWeight: 700 }}>
                            +{e.xp} XP
                          </span>
                          <span style={{ color: "#94A3B8", fontSize: 11 }}>
                            {e.created_at
                              ? new Date(e.created_at).toLocaleDateString("fr-CA",
                                  { day: "numeric", month: "short" })
                              : ""}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </Card>
          </>
        )}
      </div>
    </PageShell>
  );
}
